import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EncContinuousFilterField, SelectionMode } from '@mode-vegas/vegas';
import { ButtonComponent, IconComponent, TooltipDirective, trackByFactory } from '@mode/capra';
import {
  LegacyReportFilterControl,
  MultiValueSelection,
  RangeValueFilterControl,
  ReportFilterControlHelpers,
} from '@mode/filters/util';
import { EMap } from '@mode/reports/sandbox/contract';
import {
  FeatureFlag,
  FeatureFlagsFacade,
  MAX_SELECTED_FILTER_OPTS,
  ModeModel,
  RangeValueOption,
  ReportTypes,
} from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { Map as ImmutableMap } from 'immutable';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { FilterContainerComponent } from '../filter-container/filter-container.component';

@Component({
  selector: 'mode-filter-panel',
  standalone: true,
  imports: [CommonModule, FilterContainerComponent, ButtonComponent, TooltipDirective, IconComponent, DndModule],
  templateUrl: './filters-container.component.html',
  styleUrls: ['./filters-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersContainerComponent implements OnChanges {
  @Input() filterOptions!: EMap<ReportTypes.ReportFilterOptions>;
  @Input() filters!: ReportTypes.ReportFilterControl[];
  @Input() optCounts: EMap<number> = {};
  @Input() selectedOptCounts: EMap<number> = {};
  @Input() isFilterPanelOpen = true;
  @Input() showSettingsMenu = false;
  @Input() isLoading = true;
  @Input() orphanedFilters = new Set<string>();
  @Input() showSetDefault = false;
  @Input() model!: ModeModel;
  @Input() ffDropdownEnabled = false;
  @Input() ffDragAndDropEnabled = false;
  @Input() ffDisplayRelevantValuesEnabled = false;
  @Input() buttonPosition = 'bottom';
  @Input() isViewer = false;
  @Input() isEmbedded = false;

  @Output() updateReportFilterType = new EventEmitter<{
    filter: LegacyReportFilterControl;
    filterType: ReportTypes.ReportFilterType;
    defaultSelection?: ReportTypes.FilterSelection;
    applyFilters: boolean;
  }>();
  @Output() selectControlTypeChange = new EventEmitter<{
    filter: LegacyReportFilterControl;
    controlType: ReportTypes.SelectControlType;
  }>();
  @Output() filtersApply = new EventEmitter<LegacyReportFilterControl[]>();
  @Output() filtersReset = new EventEmitter<LegacyReportFilterControl[]>();
  @Output() filtersSetAsDefault = new EventEmitter<LegacyReportFilterControl[]>();
  @Output() filterMove = new EventEmitter<{ filter: LegacyReportFilterControl; nextToken: string | null }>();
  @Output() filterDelete = new EventEmitter<LegacyReportFilterControl>();
  @Output() filterSelectionChange = new EventEmitter<LegacyReportFilterControl>();
  @Output() filtersApplyAndGenerateLink = new EventEmitter<LegacyReportFilterControl[]>();

  @Output() showAll = new EventEmitter<{
    filter: LegacyReportFilterControl;
    showAll: boolean;
    selection?: ReportTypes.FilterValueSelection;
    persist: boolean;
  }>();
  @Output() showRelevantValues = new EventEmitter<{
    filterToken: string;
    showRelevantValues: boolean;
  }>();

  uiFilters = new Map<string, LegacyReportFilterControl>();
  protected discreteFilterOptByToken: {
    [token: string]: ImmutableMap<ReportTypes.ReportFilterValue, ReportTypes.DiscreteValueOption>;
  } = {};
  currentMultiOptCounts: { [token: string]: number } = {}; // the realtime selected counts for multi filters
  isCalcByToken: { [token: string]: boolean } = {}; // dictionary that stores whether a filter is on a calculated field
  isDirty = false;
  draggingFilter: ReportTypes.ReportFilterControl | null = null;
  readonly trackByFn = trackByFactory<ReportTypes.ReportFilterControl>(({ token }) => token);
  enableApplyAndGenerateLink$ = this.featureFlagsFacade.asObservable(FeatureFlag.ReportFiltersApplyAndGenerateLink);

  constructor(private featureFlagsFacade: FeatureFlagsFacade) {}
  ngOnChanges(changes: SimpleChanges) {
    if (changes.filters?.currentValue && this.filters?.length > 0) {
      this.uiFilters = new Map(
        this.filters.map((filter) => [filter.token, ReportFilterControlHelpers.toControl(filter)])
      );

      if (this.model != null) {
        this.isCalcByToken = {};
        this.filters.forEach((filter) => {
          this.isCalcByToken[filter.token] = this.model.views.some((view) =>
            CompiledField.formulaHasCalcAttr(view, filter.formula)
          );
        });
      }

      const multiFilters = this.filters.filter(
        (filter): filter is ReportTypes.MultiValueFilterControl =>
          filter.filterType === ReportTypes.ReportFilterType.Multi
      );

      this.currentMultiOptCounts = multiFilters.reduce(
        (counts, filter) => {
          counts[filter.token] = filter.selected.values.length;
          return counts;
        },
        {} as { [token: string]: number }
      );

      this.checkDirtyFilters();
    }

    // Technically, we can end up building isCalcToken twice, but allows it to handle adding a filter or the model changing
    // Ideally, if report filters became more RxJS-based, it'd be taken care of more cleanly
    if (changes.model?.currentValue) {
      this.isCalcByToken = {};
      this.filters.forEach((filter) => {
        this.isCalcByToken[filter.token] = this.model.views.some((view) =>
          CompiledField.formulaHasCalcAttr(view, filter.formula)
        );
      });
    }

    if (changes.filterOptions && this.filterOptions != null) {
      for (const [token, options] of Object.entries(this.filterOptions)) {
        if (options.filterType !== ReportTypes.ReportFilterType.Range) {
          // Map of value -> option by filter token
          this.discreteFilterOptByToken[token] = ImmutableMap(
            options.values.map((option) => {
              return [option.value, option];
            })
          );
        }
      }
    }
  }

  private onSelectionChange(token: string): void {
    const filter = this.uiFilters.get(token);
    if (filter?.token === token) {
      this.filterSelectionChange.emit(filter);
    }
  }

  onSelectChange(token: string, selection: ReportTypes.DiscreteFilterSelectionTypes) {
    const record = this.uiFilters.get(token);

    if (record && record.filterType === ReportTypes.ReportFilterType.Single) {
      if (selection.type === ReportTypes.DiscreteSelectionType.Select) {
        this.uiFilters.set(
          token,
          record.with({
            selected: record.selected.with({ value: selection.value, all: false }),
          })
        );
      } else {
        const all = selection.type === ReportTypes.DiscreteSelectionType.All;
        this.uiFilters.set(
          token,
          record.with({
            selected: record.selected.with({ value: null, all }),
          })
        );
      }

      this.checkDirtyFilters();
      this.onSelectionChange(token);
    }
  }

  onRangeChange(filter: EncContinuousFilterField, token: string) {
    const record = this.uiFilters.get(token) as RangeValueFilterControl;

    if (record) {
      const options = new RangeValueOption({
        min: filter.range.min,
        max: filter.range.max,
      });

      this.uiFilters.set(
        token,
        record.with({
          selected: options,
        })
      );

      this.checkDirtyFilters();
      this.onSelectionChange(token);
    }
  }

  private getSelectionMode(selected: MultiValueSelection, all: boolean): SelectionMode {
    if (this.isExcludeType(selected)) {
      return all ? SelectionMode.EXCLUDE_ALL : SelectionMode.EXCLUDE;
    }

    return all ? SelectionMode.ALL : SelectionMode.INCLUDE;
  }

  onSelectControlTypeChange(filter: ReportTypes.ReportFilterControl, controlType: ReportTypes.SelectControlType) {
    this.selectControlTypeChange.emit({
      filter: ReportFilterControlHelpers.toControl(filter),
      controlType,
    });
  }

  onMoveFilterToIndex(filterToken: string, newIndex: number) {
    if (filterToken) {
      const filterControl = this.uiFilters.get(filterToken);
      const next = this.filters[newIndex];
      if (filterControl && next) {
        this.filterMove.emit({ filter: filterControl, nextToken: next.token });
      }
    }
  }

  onMoveFilter(filterToken: string, move: 'top' | 'bottom' | 'up' | 'down') {
    const index = this.filters.findIndex((f) => f.token === filterToken);
    const filterControl = this.uiFilters.get(filterToken);

    if (filterControl) {
      switch (move) {
        case 'top':
          this.filterMove.emit({ filter: filterControl, nextToken: this.filters[0].token });
          break;
        case 'bottom':
          this.filterMove.emit({ filter: filterControl, nextToken: null });
          break;
        case 'up':
          const prev = this.filters[index - 1];
          if (prev) {
            this.filterMove.emit({ filter: filterControl, nextToken: prev.token });
          }
          break;
        case 'down':
          const next = this.filters[index + 2];
          this.filterMove.emit({ filter: filterControl, nextToken: next?.token });
          break;
      }
    }
  }

  onDeleteFilter(filterToken: string) {
    const filter = this.uiFilters.get(filterToken);
    if (filter) {
      this.filterDelete.emit(filter);
    }
  }

  private isExcludeType(selected: MultiValueSelection): boolean {
    return selected.selectionMode === SelectionMode.EXCLUDE || selected.selectionMode === SelectionMode.EXCLUDE_ALL;
  }

  onMultiSelectChange(
    filter: ReportTypes.MultiValueFilterControl,
    selection: ReportTypes.DiscreteFilterSelectionTypes,
    searchTerm: string
  ) {
    const record = this.uiFilters.get(filter.token);
    if (record?.filterType === ReportTypes.ReportFilterType.Multi) {
      let selected = record.selected;

      switch (selection.type) {
        case ReportTypes.DiscreteSelectionType.Select: {
          if (selected.all) {
            // Set values = to all filter options except the selection.
            selected = selected.with({
              selectionMode: SelectionMode.INCLUDE,
              values: selection.selected
                ? selected.values.clear().add(selection.value)
                : this.discreteFilterOptByToken[record.token]
                    .remove(selection.value)
                    .map((opt) => opt.value)
                    .toSet(),
              all: false,
            });
          } else {
            selected = selected.with({
              selectionMode: SelectionMode.INCLUDE,
              values: selection.selected
                ? selected.values.add(selection.value)
                : selected.values.remove(selection.value),
              all: false,
            });

            /**
             * Transitional logic. For performance reasons, in the future most of this logic will be done
             * in the parent app because the iframe won't be receiving all the filter options.
             * For now, we'll be doing it here, but some steps will be taken to ensure an easier
             * transition to a version where only visible (i.e. first 250 or 1000) filter options
             * are provided to the iframe.
             */
            const totalCount = this.optCounts[filter.token]; // Total number of options in the domain
            const selectedCount = this.selectedOptCounts[filter.token]; // Count of selected options sent in the last Flamingo query
            const currentOptCount = selected.values.count(); // Count of selected options as seen by the user in the filter panel
            const embedOptCount = filter.selected.values.length; // Count of selected options as received by the iframe (i.e. out of the max 250 given, N selected)

            /**
             * Check if all should be checked. The theory is that we should be able to determine
             * if all the options are selected given we know how many more options we are selecting,
             * which we can add to the total number of selected options to see what the new selected
             * total should be.
             */
            if (totalCount === selectedCount + currentOptCount - embedOptCount) {
              /**
               * Safeguard against disjoint selections being flagged as ALL. This can happen
               * with display relevant values enabled. For example:
               *
               * DomainValues {
               *   A: { a, b, c, d, e },
               *   B: { a, b, c, d, e },
               * }
               *
               * ☝🏼 values are the same to keep pair-tracking simpler ..
               *
               * FilterB selection: { a, b, c }
               * FilterA selection: { a, b }
               *
               * After refining the selection of FilterA, FilterB still has {a,b,c}
               * selected. The [relevant values] domain for FilterB, however, is now
               * {a,b}. If we then deselect "b" in FilterB, its selection count (2)
               * matches the domain value count (2) but it is a disjoint selection:
               * Domain {a,b} -> Selection {a,c}. This check ensures we do not
               * map this to an ALL selection
               */
              const domain = this.discreteFilterOptByToken[record.token];
              if (selected.values.equals(domain.keySeq().toSet())) {
                selected = selected.with({
                  selectionMode: SelectionMode.ALL,
                  values: selected.values.clear(),
                  all: true,
                });
              }
            }
          }

          break;
        }
        case ReportTypes.DiscreteSelectionType.All: {
          let values = selected.values.clear();
          let all = true;

          // No need to extend the selection if everything is already selected.
          // Otherwise, add values matching the search term to the current set
          // of selected values (if any)
          if (!selected.all && searchTerm) {
            const allValues = this.discreteFilterOptByToken[record.token];
            const withMatches = allValues
              .filter((opt) => includesTerm(opt.label, searchTerm) || selected.values.has(opt.value))
              .map((opt) => opt.value)
              .toSet();

            // Selecting a subset of values
            if (withMatches.size < allValues.size) {
              values = withMatches;
              all = false;
            }
          }

          selected = selected.with({
            selectionMode: this.getSelectionMode(selected, all),
            values,
            all,
          });
          break;
        }
        case ReportTypes.DiscreteSelectionType.None: {
          let values = selected.values.clear();

          // Deselecting values matching the search term
          if (searchTerm) {
            const termSet = selected.all
              ? // Testing all values
                this.discreteFilterOptByToken[record.token]
              : // Testing subset of values (if any)
                this.discreteFilterOptByToken[record.token].filter((opt) => {
                  return selected.values.has(opt.value);
                });

            values = termSet
              .filter((opt) => !includesTerm(opt.label, searchTerm))
              .map((opt) => opt.value)
              .toSet();
          }

          selected = selected.with({
            selectionMode: this.getSelectionMode(selected, false),
            all: false,
            values,
          });
          break;
        }
        case ReportTypes.DiscreteSelectionType.Exclude:
          selected = selected.with({
            selectionMode: SelectionMode.EXCLUDE,
          });
          break;
        case ReportTypes.DiscreteSelectionType.ExcludeAll:
          selected = selected.with({
            selectionMode: SelectionMode.EXCLUDE_ALL,
          });
          break;
        case ReportTypes.DiscreteSelectionType.Include:
          selected = selected.with({
            selectionMode: selected.all ? SelectionMode.ALL : SelectionMode.INCLUDE,
          });
          break;
      }

      this.currentMultiOptCounts[filter.token] = selected.values.size;
      this.uiFilters.set(
        filter.token,
        record.with({
          selected,
        })
      );

      this.checkDirtyFilters();
      this.onSelectionChange(filter.token);
    }
  }

  onUpdateReportFilterType(filter: ReportTypes.ReportFilterControl, filterType: ReportTypes.ReportFilterType) {
    // Reset selections when applicable
    let selection: ReportTypes.FilterSelection | undefined;
    let applyFilters = true;
    switch (filterType) {
      case ReportTypes.ReportFilterType.Single:
        selection = { all: true, value: null };
        // Don't apply filters if we already have "all" selected
        applyFilters = ReportFilterControlHelpers.isMultiValueControlFilter(filter) && !filter.selected.all;
        break;
      case ReportTypes.ReportFilterType.Multi:
        selection = { all: true, values: [] };
        applyFilters = ReportFilterControlHelpers.isSingleValueControlFilter(filter) && !filter.selected.all;
        break;
      case ReportTypes.ReportFilterType.Range:
        selection = { min: null, max: null };
        applyFilters = true;
        break;
    }

    this.updateReportFilterType.emit({
      filter: ReportFilterControlHelpers.toControl(filter),
      filterType,
      defaultSelection: selection,
      applyFilters,
    });
  }

  apply() {
    this.filtersApply.emit([...this.uiFilters.values()]);
  }

  applyAndGenerateLink() {
    this.filtersApplyAndGenerateLink.emit([...this.uiFilters.values()]);
  }

  onResetAllFilters() {
    this.filtersReset.emit([...this.uiFilters.values()]);
  }

  onResetFilter(filterToken: string) {
    const filter = this.uiFilters.get(filterToken);
    if (filter) {
      this.filtersReset.emit([filter]);
    }
  }

  onShowAllChanged(filterToken: string, showAll: boolean) {
    const filter = this.uiFilters.get(filterToken);
    if (filter) {
      let persist = false;
      let selection: ReportTypes.FilterValueSelection | undefined;
      if (filter.filterType === ReportTypes.ReportFilterType.Single) {
        const options = this.filterOptions[filterToken];
        if (options != null && options.filterType === ReportTypes.ReportFilterType.Single) {
          // if "all" is true, then showAll will be false
          if (filter.default.all) {
            /**
             * When hiding select all with all selected, have to persist the new selection because
             * "all" won't be valid afterwards.
             **/
            persist = true;
          }

          if (filter.selected.all === false) {
            selection = filter.selected.toJSON();
          } else {
            selection = {
              all: false,
              value: options.values[0]?.value,
            };
          }
        }
      } else if (filter.filterType === ReportTypes.ReportFilterType.Multi) {
        const options = this.filterOptions[filterToken];
        if (options != null && options.filterType === ReportTypes.ReportFilterType.Multi) {
          if (filter.selected.all === false) {
            selection = {
              all: filter.selected.all,
              values: filter.selected.values.toArray(),
            };
          } else {
            selection = {
              all: true,
              values: [],
            };
          }
        }
      }

      this.showAll.emit({ filter, showAll, selection, persist });
    }
  }

  onShowRelevantValuesChanged(filterToken: string, showRelevantValues: boolean) {
    this.showRelevantValues.emit({ filterToken, showRelevantValues });
  }

  setDefault() {
    this.filtersSetAsDefault.emit([...this.uiFilters.values()]);
  }

  hasTooManySelected(filterToken: string): boolean {
    return this.currentMultiOptCounts[filterToken] > MAX_SELECTED_FILTER_OPTS;
  }

  hasOrphanedFilters(filterToken: string): boolean {
    return this.orphanedFilters.has(filterToken);
  }

  isCalcField(filterToken: string): boolean {
    return this.isCalcByToken[filterToken];
  }

  checkDirtyFilters() {
    this.isDirty = Array.from(this.uiFilters.values()).some((filter) =>
      ReportFilterControlHelpers.isDirtyFilter(filter)
    );
  }

  onDrop(event: DndDropEvent) {
    if (this.draggingFilter) {
      switch (event.index) {
        case 0:
          this.onMoveFilter(this.draggingFilter.token, 'top');
          break;
        case this.filters.length:
          this.onMoveFilter(this.draggingFilter.token, 'bottom');
          break;
        default:
          if (event.index) {
            const currentIndex = this.filters.findIndex((filter) => filter.token === this.draggingFilter?.token);
            if (event.index !== currentIndex) {
              this.onMoveFilterToIndex(this.draggingFilter.token, event.index);
            }
          }
      }
    }
  }

  onDragEnd() {
    this.draggingFilter = null;
  }

  onDragStart(filter: ReportTypes.ReportFilterControl) {
    this.draggingFilter = filter;
  }
}

/**
 * Returns true if the value includes term, false otherwise. The test is case-insensitive
 */
function includesTerm(value: ReportTypes.ReportFilterValue, term: string): boolean {
  return tolower(value).includes(tolower(term));
}

/** @internal coerces input to a string then lowercases the result */
function tolower(value: unknown): string {
  return String(value).toLowerCase();
}
